import { useCallback, useEffect, useRef, useState } from "react";
import ReactTooltip from "@tippyjs/react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "./cn";

/* The tab strip both Create Notes panels wear: one per template on the right,
   Transcript / Insights on the left. Full-height buttons flush with the panel
   header's own rule, so the active tab's 2px accent bar reads as part of it.

   Every tab carries a hairline border, not just the open one — the strip is a
   row of chips you can drag and dismiss, and you cannot aim at something whose
   edges you cannot see.

   The strip scrolls rather than wraps — a panel header is a fixed h-12 row and
   a wrapping strip would push the box down by a line the moment a clinician
   picks a fourth template. Chevrons appear only when there is somewhere to
   scroll to, and disable at each end rather than vanishing, so the control does
   not jump under the cursor mid-click.

   Selecting a tab scrolls it into view, which matters when the selection comes
   from somewhere else — adding a template focuses it, and it may be off-strip.

   Reorder is HTML5 drag-and-drop, which is mouse-only: there is no touch
   equivalent without a pointer-event drag that would fight this scroller for
   the same gesture. Reordering is a desktop affordance; the strip stays fully
   usable without it. */

const SCROLL_STEP = 160;
const SCROLL_DURATION = 260;

const PanelTabs = ({
  tabs,
  activeId,
  onSelect,
  onReorder,
  onRemove,
  removeLabel = "Remove",
  ariaLabel,
  className,
}) => {
  const scrollerRef = useRef(null);
  const [overflow, setOverflow] = useState({ scrollable: false, atStart: true, atEnd: true });
  /* The dragged id lives in a ref, and the state is only there to drive the
     visuals. Reading it from state inside dragover/drop is a race: the first
     dragover can arrive before React has committed the dragstart, and a
     dragover that does not call preventDefault tells the browser this is not a
     drop target — so the very first hover silently disarms the drop. */
  const dragIdRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // 1px of slack: sub-pixel layout leaves scrollWidth a hair over clientWidth
    // on strips that actually fit, which would show two dead chevrons.
    const next = {
      scrollable: el.scrollWidth - el.clientWidth > 1,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft >= el.scrollWidth - el.clientWidth - 1,
    };
    // Trackpad scrolling fires this continuously; only re-render on a real change.
    setOverflow((prev) =>
      prev.scrollable === next.scrollable && prev.atStart === next.atStart && prev.atEnd === next.atEnd
        ? prev
        : next
    );
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    measure();

    // Measure again once the brand faces load. Every tab is a text label, so the
    // first measurement is taken against the fallback stack and can be far
    // enough out to decide "no overflow" on a strip that does overflow — which
    // is how the chevrons went missing entirely. `fonts.ready` is a promise, not
    // a rendering callback, so it still resolves in a backgrounded tab.
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) measure();
    });

    // The strip's width changes with the panel, not just the window — the
    // sidebar collapsing resizes it without a resize event.
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [measure, tabs.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // No `behavior` here either — see scrollBy. This one only ever needs to
    // land the tab on screen, so an instant correction is the right result.
    const activeEl = el.querySelector('[data-tab-active="true"]');
    if (activeEl) activeEl.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeId]);

  /* The scroll is animated by hand, and that is deliberate.
     `behavior: "smooth"` is silently ignored on this nested scroller — the
     element does not move at all, so the chevrons are dead — and CSS
     `scroll-behavior: smooth` fails the same way, which also poisons
     `behavior: "auto"` because auto defers to the CSS. Assigning `scrollLeft`
     is the only form that reliably moves it. Do not "simplify" this back to
     either of those.

     rAF is suspended while the document is hidden (this bit the accordion once
     already), so a hidden document jumps straight to the destination.

     Both paths call measure() when they land. The `scroll` event alone is not
     enough: a programmatic scroll in a hidden document does not deliver one, and
     the chevrons were left reporting the opposite of the truth — the left one
     disabled at the far right of the strip, which is unclickable, not just
     wrong. onScroll stays for trackpad scrolling, which does fire. */
  const scrollBy = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;

    const from = el.scrollLeft;
    const to = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, from + direction * SCROLL_STEP));
    if (from === to) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || document.hidden) {
      el.scrollLeft = to;
      measure();
      return;
    }

    const startedAt = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - startedAt) / SCROLL_DURATION);
      // Ease out, the shape of --ns-ease-ui.
      el.scrollLeft = from + (to - from) * (1 - Math.pow(1 - t, 3));
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        measure();
      }
    };
    requestAnimationFrame(step);
  };

  const endDrag = () => {
    dragIdRef.current = null;
    setDragId(null);
    setOverId(null);
  };

  const handleDrop = (targetId) => {
    const sourceId = dragIdRef.current;
    if (sourceId !== null && sourceId !== targetId) onReorder(sourceId, targetId);
    endDrag();
  };

  if (!tabs.length) return null;

  const isReorderable = typeof onReorder === "function" && tabs.length > 1;
  // The owner's rule: a clinician must always be left with at least one
  // template, so the last tab keeps its label but loses its dismiss control.
  const canRemove = typeof onRemove === "function" && tabs.length > 1;

  const chevronClasses = (disabled) =>
    cn(
      "flex h-7 w-6 flex-shrink-0 items-center justify-center self-center rounded-ns-sm",
      "transition-colors duration-ui ease-ui",
      disabled
        ? "cursor-not-allowed text-shade-900"
        : "text-muted hover:bg-accent-tint hover:text-accent"
    );

  return (
    <div className={cn("flex min-w-0 items-stretch", className)}>
      {overflow.scrollable && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          disabled={overflow.atStart}
          aria-label="Scroll tabs left"
          className={chevronClasses(overflow.atStart)}
        >
          <ChevronLeft size={16} />
        </button>
      )}

      <div
        ref={scrollerRef}
        role="tablist"
        aria-label={ariaLabel}
        onScroll={measure}
        className="scrollbar-none flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto py-1.5"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          const isDragging = dragId === tab.id;
          const isDropTarget = overId === tab.id && dragId !== null && !isDragging;

          return (
            <div
              key={tab.id}
              draggable={isReorderable}
              onDragStart={(e) => {
                if (!isReorderable) return;
                dragIdRef.current = tab.id;
                setDragId(tab.id);
                e.dataTransfer.effectAllowed = "move";
                // Firefox will not start a drag without payload on the transfer.
                e.dataTransfer.setData("text/plain", String(tab.id));
              }}
              onDragOver={(e) => {
                if (!isReorderable || dragIdRef.current === null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setOverId(tab.id);
              }}
              onDrop={(e) => {
                if (!isReorderable) return;
                e.preventDefault();
                handleDrop(tab.id);
              }}
              onDragEnd={endDrag}
              /* The chip is the wrapper, not the tab button: the dismiss control
                 has to be a real <button>, and a button inside a button is
                 invalid HTML. Both live side by side inside one bordered box. */
              className={cn(
                "relative flex max-w-[190px] flex-shrink-0 items-stretch rounded-ns border border-solid",
                "transition-colors duration-ui ease-ui",
                isReorderable && "cursor-grab active:cursor-grabbing",
                isDragging && "opacity-40",
                isActive ? "border-rule-strong bg-accent-tint" : "border-rule hover:bg-shade-50"
              )}
            >
              {isDropTarget && (
                <span
                  aria-hidden="true"
                  className="absolute -left-1 top-0 bottom-0 w-0.5 rounded-full bg-accent"
                />
              )}

              {/* Tippy rather than the native title attribute: chip labels
                  truncate at 190px, so the full template name is often only
                  available here, and a half-second browser tooltip is not a
                  reasonable way to read it. */}
              <ReactTooltip content={tab.label}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-tab-active={isActive}
                  onClick={() => onSelect(tab.id)}
                  className={cn(
                    "min-w-0 rounded-ns font-body text-ui-xs transition-colors duration-ui ease-ui",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent",
                    canRemove ? "pl-3 pr-1" : "px-3",
                    isActive ? "font-medium text-accent" : "text-muted"
                  )}
                >
                  <span className="block truncate">{tab.label}</span>
                </button>
              </ReactTooltip>

              {canRemove && (
                <button
                  type="button"
                  aria-label={`${removeLabel} ${tab.label}`}
                  draggable={false}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(tab.id);
                  }}
                  // Without this the X swallows the gesture and the chip cannot
                  // be picked up from its right-hand end.
                  onDragStart={(e) => e.preventDefault()}
                  className={cn(
                    "mr-1.5 flex flex-shrink-0 items-center self-center rounded-full",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  )}
                >
                  <ReactTooltip content={`${removeLabel} ${tab.label}`}>
                    <span
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-full border border-solid",
                        "transition-colors duration-ui ease-ui",
                        isActive
                          ? "border-accent/30 text-accent hover:bg-accent hover:text-white"
                          : "border-rule-strong text-shade-900 hover:border-muted hover:bg-muted hover:text-white"
                      )}
                    >
                      <X size={10} strokeWidth={2.5} />
                    </span>
                  </ReactTooltip>
                </button>
              )}

              {/* Sits on the header's own hairline, not above it, so the active
                  tab and the rule read as one shape. */}
              <span
                aria-hidden="true"
                className={cn(
                  "absolute inset-x-1 -bottom-[7px] h-0.5 rounded-full bg-accent",
                  "transition-opacity duration-ui ease-ui",
                  isActive ? "opacity-100" : "opacity-0"
                )}
              />
            </div>
          );
        })}
      </div>

      {overflow.scrollable && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          disabled={overflow.atEnd}
          aria-label="Scroll tabs right"
          className={chevronClasses(overflow.atEnd)}
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
};

export default PanelTabs;
